<template>
  <div class="pa-6">
    <Loader :messageSelector="messageSelector" :overlay="overlay" />
    <v-form ref="form" v-model="valid">
      <!-- Déclaration -->
      <fieldset>
        <legend class="text-h6 text-sm-h5">Déclaration de l'activité réservée 3.6.10</legend>
        <div class="legalSection">
          <!-- Réforme -->
          <RessourceInfoBulle code="RE-AR-1" />
          <p class="mentionLegal font-weight-bold">
            Dans le cadre de l'exercice de vos activités professionnelles, peu importe la fréquence ou le milieu d'exercice, procédez-vous à
            l'évaluation psychosociale d'une personne dans le cadre de la tutelle au majeur ou d'un mandat de protection (incluant également
            la représentation temporaire du majeur inapte)?
          </p>
          <v-autocomplete
            v-model="getActiviteReservee.pratiqueActivite"
            :item-text="itemText"
            :item-value="itemValue"
            :items="listePratiqueActivite"
            :rules="rules.pratiqueActivite"
            class="cssRequired"
            label="Réponse"
            required
            return-object
            @change="modelChange()"
          ></v-autocomplete>

          <div v-if="getActiviteReservee.pratiqueActivite">
            <div v-if="pratiqueActivite">
              <p class="mentionLegal">
                Veuillez SVP préciser votre situation actuelle ou mettre à jour votre choix précédent au regard des activités de formation
                continue que vous avez réalisées en lien avec l'exercice de cette activité réservée.
              </p>
              <v-radio-group v-model="getActiviteReservee.statutCompletion" :rules="rules.statutCompletion" required return-object>
                <v-radio v-for="statut in listeStatutCompletion" :key="statut.id" :label="`${statut.lang00}`" :value="statut"></v-radio>
              </v-radio-group>
              <p class="mentionLegal">
                Pour plus d'informations sur l'obligation de suivre ces activités de formation continue, les modalités d'inscription et les
                dispensateurs autorisés par l'OTSTCFQ, veuillez visiter le site,
                <a
                  class="link-trim-space"
                  href="https://evaluerproteger.otstcfq.org/evaluer-proteger/renseignements-pour-les-membres/obligations/"
                  target="_blank"
                >
                  Évaluer, protéger </a
                >.
              </p>
              <p class="text-decoration-underline">
                RAPPEL IMPORTANT: La réalisation de ces formations est réglementée et l'information consignée dans cette
                déclaration sera utilisée par l'Ordre aux fins de vérification et application des dispositions
                réglementaires.
                Le cas échéant, veuillez vous assurer que l'activité de formation que vous attestez avoir réalisée se
                trouve dans votre déclaration de formation continue en ligne.
              </p>
              <p class="mb-6 warning-text">
                * intitulée « Évaluation psychosociale en rapport avec les régimes de protection de la personne majeure » et d'une durée de
                21 heures, offerte par l'Ordre entre 2011 et le 31 mars 2022.
                <br />
                ** offerte ou autorisée par l'Ordre depuis le 1<sup>er</sup> avril 2022, incluant les contenus concernant les nouvelles dispositions introduites par le PL18.
              </p>
            </div>
            <div v-else>
              <p class="mentionLegal">
                En déclarant de ne pas exercer l'activité réservée consistant à procéder à l'évaluation psychosociale d'une personne dans le
                cadre de la tutelle au majeur ou d'un mandat de protection (incluant également la représentation temporaire du majeur
                inapte),
                <span class="font-weight-bold">vous êtes dispensés de l'obligation de suivre une formation à ce sujet</span>.
              </p>
              <p class="font-weight-bold font-italic">QUE SE PASSE-T-IL SI JE NE DÉCLARE PAS L'EXERCICE DE CETTE ACTIVITÉ?</p>
              <p class="mentionLegal">
                Considérant la résolution du Conseil d'administration de l'OTSTCFQ imposant une formation obligatoire pour tous les membres
                T.S. exerçant cette activité professionnelle réservée, ceux qui l'accompliraient sans en avoir avisé l'Ordre par le biais de
                la présente déclaration s'exposent à en répondre auprès des différents services dans le cadre de leurs mandats respectifs.
              </p>
              <p class="font-weight-bold font-italic">QUE SE PASSE-T-IL SI MA SITUATION CHANGE?</p>
              <p class="mentionLegal">
                En cas de changement au niveau de l'exercice de l'activité réservée consistant à procéder à l'évaluation psychosociale d'une
                personne dans le cadre de la tutelle au majeur ou d'un mandat de protection (incluant également la représentation temporaire
                du majeur inapte), nous vous prions de réviser la présente déclaration dans les meilleurs délais en passant par « Mon espace
                ».
                <br />
                <br />
                Pour plus d'informations veuillez visiter le site
                <a
                  class="link-trim-space"
                  href="https://evaluerproteger.otstcfq.org/evaluer-proteger/renseignements-pour-les-membres/obligations/"
                  target="_blank"
                >
                  Évaluer, protéger </a
                >.
              </p>
            </div>
          </div>
        </div>
      </fieldset>

      <!-- Activités -->
      <fieldset>
        <legend class="text-h6 text-sm-h5">Activités réservées</legend>
        <div class="legalSection">
          <RessourceInfoBulle code="RE-AR-2" />

          <v-list flat subheader>
            <v-list-item-group>
              <list-item-with-checkbox
                v-for="fd in activitesReservees"
                v-bind:key="fd.id"
                :value="fd.value"
                :texte="fd.lang00"
                :disabled="fd.disabled"
                :property-name="fd.id.toString()"
                @updateChoix="updateChoix"
              />
            </v-list-item-group>
          </v-list>
          <v-input :value="this.activitesReservees.filter((ar) => ar.value)" :rules="rules.activitesReserveesCompletion"></v-input>
        </div>
        <div v-if="isEvaluationAdoptionEnfant">
          <p class="mentionLegal font-weight-bold">
            Exercez-vous cette activité auprès de personnes voulant adopter hors Québec?
          </p>
          <v-autocomplete
            v-model="getActiviteReservee.activiteAdoptionHorsQuebec"
            :item-text="itemText"
            :item-value="itemValue"
            :items="listeActiviteAdoptionHorsQuebec"
            :rules="rules.activiteAdoptionHorsQuebec"
            class="cssRequired"
            label="Réponse"
            required
            return-object
          ></v-autocomplete>
        </div>
      </fieldset>
    </v-form>

    <!-- Buttons -->
    <div class="text-center mt-6 d-flex justify-space-between">
      <v-btn color="primary" @click="back()">Retour</v-btn>
      <v-btn :disabled="!valid" color="primary" @click="saveAndContinue()">Suivant</v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex"
import Loader from "../Loader"
import NavigationPageFormulaireInscriptionMixin from "@/mixins/navigationPageFormulaireInscriptionMixin"
import ListItemWithCheckbox from "../common/ListItemWithCheckbox"

export default {
  mixins: [NavigationPageFormulaireInscriptionMixin],
  props: {
    routeName: {
      type: String,
      default: "",
    },
  },
  components: {
    Loader: Loader,
    ListItemWithCheckbox,
  },
  data() {
    return {
      messageSelector: "",
      overlay: false,
      valid: false,
      activitesReservees: [],
      rules: {
        pratiqueActivite: [(v) => !!v || "Cette valeur est requise"],
        statutCompletion: [(v) => !!v || "Cette valeur est requise"],
        activiteAdoptionHorsQuebec: [(v) => !!v || "Cette valeur est requise"],
        activitesReserveesCompletion: [
          () => this.activitesReservees.filter((ar) => ar.value).length > 0 || "Vous devez sélectionner au moins une valeur",
        ],
      },
    }
  },
  computed: {
    ...mapGetters({
      demandeInscriptionId: "DemandeInscription/GET_DEMANDEINSCRIPTIONID",
      getActiviteReservee: "ActiviteReservee/getActiviteReservee",
      listePratiqueActivite: "Catalogue/GET_PRATIQUE_ACTIVITE",
      listeStatutCompletion: "Catalogue/GET_STATUT_COMPLETION",
      listeActivitesReservees: "Catalogue/GET_ACTIVITES_RESERVEES",
      listeActiviteAdoptionHorsQuebec: "Catalogue/getActiviteAdoptionHorsQuebec",
    }),
    pratiqueActivite() {
      return (
        this.getActiviteReservee && this.getActiviteReservee.pratiqueActivite && this.getActiviteReservee.pratiqueActivite.lang00 === "Oui"
      )
    },
    isEvaluationAdoptionEnfant() {
      return (
        this.activitesReservees
          .find((ar) => ar.id === this.$catalogues.activiteActiviteReserve.evaluationAdoptionEnfant)?.value === true
      )
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    ...mapMutations({
      setStatutCompletion: "ActiviteReservee/setStatutCompletion",
    }),
    ...mapActions({
      getCatalogue: "Catalogue/getCatalogue",
      getActiviteReserveeByDemandeInscriptionId: "ActiviteReservee/getActiviteReserveeByDemandeInscriptionId",
      updateActiviteReservee: "ActiviteReservee/updateActiviteReservee",
    }),
    async fetchItems() {
      try {
        this.messageSelector = "Loading"
        this.overlay = true

        const requests = [
          this.getCatalogue(this.$cataloguesDesc.pratiqueActiviteReserve),
          this.getCatalogue(this.$cataloguesDesc.precisionActiviteReserve),
          this.getCatalogue(this.$cataloguesDesc.activiteActiviteReserve),
          this.getCatalogue(this.$cataloguesDesc.activiteAdoptionHorsQuebec),
        ]

        if (this.demandeInscriptionId) {
          await this.getActiviteReserveeByDemandeInscriptionId(this.demandeInscriptionId)
        }

        await Promise.all(requests)

        // hack pour l'affichage du radio
        this.setStatutCompletion(this.listeStatutCompletion.find((s) => s.id === this.getActiviteReservee?.statutCompletion?.id))

        // définition des coches en fonction du catalogue et des éléments déjà sélectionnés
        let aucuneCoche = false
        this.listeActivitesReservees.forEach((property) => {
          const fd = { ...property }
          fd.value = false

          if (this.getActiviteReservee.activitesReservees.find((ar) => ar.id === fd.id)) {
            fd.value = true

            if (fd.id === this.$catalogues.activiteActiviteReserve.aucune) {
              aucuneCoche = true
            }
          }

          this.activitesReservees.push(fd)
        })
        this.sanitizeActivitesReservees(aucuneCoche)

        this.validateForm(true)

        this.overlay = false
      } catch (exception) {
        console.log("Exception", exception)
      }
    },
    async save() {
      this.validateForm()
      if (!(this.valid && this.getActiviteReservee)) {
        return
      }

      this.messageSelector = "Save"
      this.overlay = true

      // set activitesReservees from locale state
      this.getActiviteReservee.activitesReservees = this.activitesReservees.filter((fd) => fd.value)

      await this.updateActiviteReservee(this.getActiviteReservee)

      this.next()
    },
    itemText: (item) => item.lang00,
    itemValue: (item) => item.id,
    modelChange() {
      this.getActiviteReservee.statutCompletion = null
    },
    updateChoix(payload) {
      const dataId = parseInt(payload.field)
      this.activitesReservees.filter((ar) => ar.id === dataId).forEach((c) => (c.value = payload.choix)) // simplification d'écriture, doit se produire 0 ou 1 fois

      if (dataId === this.$catalogues.activiteActiviteReserve.aucune) {
        this.sanitizeActivitesReservees(payload.choix)
      }

      // vide la précision si la question est masquée
      if (!this.isEvaluationAdoptionEnfant) {
        this.getActiviteReservee.activiteAdoptionHorsQuebec = null
      }
    },
    // assure la cohérence des cases cochées
    sanitizeActivitesReservees(aucuneValue) {
      // aucune pilote la désactivation des autres cases
      this.activitesReservees
        .filter((ar) => ar.id !== this.$catalogues.activiteActiviteReserve.aucune)
        .forEach((c) => (c.disabled = aucuneValue))

      // si coché on veux vider les autres coches
      if (aucuneValue === true) {
        this.activitesReservees.filter((ar) => ar.id !== this.$catalogues.activiteActiviteReserve.aucune).forEach((c) => (c.value = false))
      }
    },
    validateForm(resetValidation = false) {
      this.valid = this.$refs.form?.validate() || false

      if (resetValidation) {
        this.$refs.form?.resetValidation()
      }
    },
    saveAndContinue() {
      this.save()
    },
  },
}
</script>

<style scoped></style>